import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { GET, POST, PUT, profile, alert, loading, Select } from "../../components/CustomComponent";
import { eatingModes, goalOptions, genderOptions, activityOptions } from "./mockData";
import { MacroBar, Chip, ReasonList, FoodCard, FeedbackBar, Section, TagInput, thaiDate, compactQuery, SOURCE_LABELS, SOURCE_ICONS, Skeleton } from "./FoodUi";

const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
const MACROS = [
  ["kcal", "Calories", "fa-solid fa-fire", "kcal", "orange"],
  ["protein", "Protein", "fa-solid fa-drumstick-bite", "g", "red"],
  ["carbs", "Carbs", "fa-solid fa-bowl-rice", "g", "gold"],
  ["fat", "Fat", "fa-solid fa-seedling", "g", "green"],
];

// หน้าแรก: GET v1/food/home คำขอเดียวได้ทั้งพลังงานวันนี้ การ์ดแนะนำ ตัวเลือกตามเวลา insight และคำถามถัดไป
export default function FoodHome() {
  const { title, description } = appModules["food_home"];
  const navigate = useNavigate();
  const firstName = profile?.employee?.first_name || profile?.full_name_th?.split(" ")[0] || profile?.email?.split("@")[0] || "";

  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [liked, setLiked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboard, setOnboard] = useState({ age: "", gender: "", height_cm: "", weight_kg: "", goal: "lose", activity_level: "3" });
  const [answer, setAnswer] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async (nextMode) => {
    const result = await GET("v1/food/home", compactQuery({ mode: nextMode }));
    if (result?.status === true) {
      setData(result.data);
      setMode(result.data.mode);
      setExcluded([]);
      setLiked(null);
      setAnswer(null);
      if (!result.data.profile_complete) setShowOnboard(true);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const pick = data?.recommendation;
  const activeMode = eatingModes.find((item) => item.key === mode) || eatingModes[1];
  const context = { mode, meal: data?.daypart?.meal, daypart: data?.daypart?.key, source: "home" };

  const sendFeedback = (action) => pick && POST("v1/food/feedback", { food_uid: pick.food.uid, action, context }, null, { silent: true });

  // "ขออีก": ขอการ์ดใหม่โดยตัดเมนูที่เพิ่งเห็นออก (ส่ง uid ที่ตัดแล้วทั้งหมดไปด้วย)
  const another = async () => {
    if (!pick || busy) return;
    setBusy(true);
    sendFeedback("skip");
    const nextExcluded = [...excluded, pick.food.uid];
    const result = await GET("v1/food/recommend", compactQuery({ mode, exclude: nextExcluded.join(",") }));
    setBusy(false);
    if (result?.status === true) {
      setExcluded(nextExcluded);
      setLiked(null);
      setData((current) => ({ ...current, recommendation: { ...result.data.recommendation, saved: false } }));
    }
  };

  const toggleSave = async () => {
    if (!pick) return;
    const saved = !pick.saved;
    setData((current) => ({ ...current, recommendation: { ...current.recommendation, saved } }));
    sendFeedback(saved ? "save" : "unsave");
  };

  const eatThis = async () => {
    if (!pick) return;
    loading(true);
    const result = await POST("v1/food/meal-logs", { food_uid: pick.food.uid, meal: data.daypart.meal, source: "recommend", mode });
    loading(false);
    if (result?.status === true) {
      await alert("", "success", "บันทึกแล้ว", `เพิ่ม "${pick.food.name}" ใน${data.daypart.title}ของวันนี้แล้ว ระบบจะเอาไปคิดพลังงานที่เหลือให้`);
      load(mode);
    }
  };

  const saveOnboard = async () => {
    if (!onboard.age || !onboard.gender || !onboard.height_cm || !onboard.weight_kg || !onboard.goal) {
      await alert("", "warning", "แจ้งเตือน", "กรอกให้ครบ 5 ข้อสั้น ๆ ระบบจะคำนวณเป้าหมายให้เอง");
      return;
    }
    loading(true);
    const result = await PUT("v1/food/profile", onboard);
    loading(false);
    if (result?.status === true) {
      setShowOnboard(false);
      await alert("", "success", "พร้อมแล้ว", `เป้าหมายของคุณ ${result.data.targets.kcal.toLocaleString()} kcal / วัน โปรตีน ${result.data.targets.protein} g ระบบจะแนะนำตามนี้`);
      load(mode);
    }
  };

  // เมนูที่ AI เสนอ (job รายวัน หรือกดขอเอง) กดรับ = เข้าฐานและถูกแนะนำได้ทันที
  const decideSuggestion = async (item, accept) => {
    const result = await POST(`v1/food/suggestions/${item.uid}/${accept ? "accept" : "reject"}`, {});
    if (result?.status === true) setData((current) => ({ ...current, suggestions: current.suggestions.filter((row) => row.uid !== item.uid) }));
  };
  const generateSuggestions = async () => {
    setGenerating(true);
    const result = await POST("v1/food/suggestions/generate", {});
    setGenerating(false);
    if (result?.status === true) setData((current) => ({ ...current, suggestions: result.data.items, can_generate: result.data.can_generate, suggested_at: result.data.suggested_at }));
  };

  // คำถามถัดไป (ถามทีละข้อ) ตอบแล้วส่งเฉพาะฟิลด์นั้น
  const question = data?.next_question;
  const answerQuestion = async (payload) => {
    loading(true);
    const result = await PUT("v1/food/profile", payload, null, { keepEmpty: true });
    loading(false);
    if (result?.status === true) load(mode);
  };
  const renderQuestion = () => {
    if (!question) return null;
    const value = answer ?? (question.type === "tags" ? [] : question.type === "choices" ? [] : question.type === "budget" ? { budget_min: "", budget_max: "" } : question.type === "times" ? { meal_breakfast: "08:00", meal_lunch: "12:00", meal_dinner: "18:00" } : "");
    return (
      <div className="food-ask mb-3">
        <div className="food-ask-head">
          <span className="food-pick-badge"><i className="fa-solid fa-robot" />AI อยากรู้เพิ่มอีกนิด</span>
          <span className="text-secondary small">กรอกแล้วคำแนะนำจะแม่นขึ้น</span>
        </div>
        <h4 className="food-ask-title">{question.title}</h4>
        <p className="food-ask-hint">{question.hint}</p>
        {question.type === "tags" ? <TagInput value={value} onChange={setAnswer} placeholder={question.hint} tone={question.key === "allergies" ? "red" : question.key === "dislikes" ? "muted" : "green"} /> : null}
        {question.type === "choices" ? (
          <div className="d-flex flex-wrap gap-1">
            {question.options.map((option) => <Chip key={option.value} tone="orange" active={value.includes(option.value)} onClick={() => setAnswer(value.includes(option.value) ? value.filter((item) => item !== option.value) : [...value, option.value])}>{option.label}</Chip>)}
          </div>
        ) : null}
        {question.type === "choice" ? (
          <div className="d-flex flex-wrap gap-1">
            {question.options.map((option) => <Chip key={option.value} tone="orange" active={value === option.value} onClick={() => setAnswer(option.value)}>{option.label}</Chip>)}
          </div>
        ) : null}
        {question.type === "budget" ? (
          <div className="food-range" style={{ maxWidth: 320 }}>
            <input type="number" className="form-control" placeholder="ต่ำสุด" value={value.budget_min} onChange={(event) => setAnswer({ ...value, budget_min: event.target.value })} />
            <span>–</span>
            <input type="number" className="form-control" placeholder="สูงสุด" value={value.budget_max} onChange={(event) => setAnswer({ ...value, budget_max: event.target.value })} />
            <span>บาท</span>
          </div>
        ) : null}
        {question.type === "times" ? (
          <div className="row g-2" style={{ maxWidth: 420 }}>
            {[["meal_breakfast", "เช้า"], ["meal_lunch", "กลางวัน"], ["meal_dinner", "เย็น"]].map(([key, label]) => (
              <div key={key} className="col-4"><label className="stg-label">{label}</label><input type="time" className="form-control" value={value[key]} onChange={(event) => setAnswer({ ...value, [key]: event.target.value })} /></div>
            ))}
          </div>
        ) : null}
        <div className="food-ask-actions">
          <button
            type="button"
            className="btn stg-btn stg-btn--primary stg-btn--sm"
            onClick={() => {
              if (question.type === "tags" || question.type === "choices") return value.length ? answerQuestion({ [question.key]: value }) : alert("", "warning", "แจ้งเตือน", "เพิ่มอย่างน้อย 1 รายการ หรือกด \"ไม่มี\"");
              if (question.type === "choice") return value ? answerQuestion({ [question.key]: value }) : alert("", "warning", "แจ้งเตือน", "เลือก 1 ข้อ");
              if (question.type === "budget") return value.budget_min && value.budget_max ? answerQuestion({ budget_min: Number(value.budget_min), budget_max: Number(value.budget_max) }) : alert("", "warning", "แจ้งเตือน", "กรอกงบต่ำสุดและสูงสุด");
              if (question.type === "times") return answerQuestion(value);
              return null;
            }}
          >
            <i className="fa-solid fa-check" />บันทึกคำตอบ
          </button>
          {question.allow_none ? <button type="button" className="btn stg-btn stg-btn--ghost stg-btn--sm" onClick={() => answerQuestion({ [question.key]: ["ไม่มี"] })}>ไม่มี</button> : null}
          <button type="button" className="btn stg-btn stg-btn--ghost stg-btn--sm" onClick={() => navigate("/food/profile")}>ไปกรอกที่หน้าโปรไฟล์</button>
        </div>
      </div>
    );
  };

  return (
    <FoodShell title={title} description={description}>
      <div className="food-hero mb-3">
        <div className="food-hero-main">
          <p className="food-hero-eyebrow">{thaiDate()}</p>
          <h1 className="food-hero-title">สวัสดี{firstName ? ` ${firstName}` : ""} 👋</h1>
          {data ? (
            <p className="food-hero-text">
              วันนี้คุณกินไปแล้ว <strong>{data.today.count} รายการ</strong> ({data.today.kcal.toLocaleString()} kcal) เหลือพลังงานอีกประมาณ <strong>{Math.max(0, data.today.remaining_kcal).toLocaleString()} kcal</strong>
              {data.budget.daily ? <> · ใช้เงินไป <strong>฿{data.today.price}</strong> จากงบ ฿{data.budget.daily}</> : null}
              {" "}ระบบเอาสิ่งที่กินไปแล้วมาคิดก่อนแนะนำมื้อถัดไปเสมอ
            </p>
          ) : (
            <p className="food-hero-text">กำลังโหลดข้อมูลวันนี้...</p>
          )}
          <div className="food-hero-actions">
            <button type="button" className="btn stg-btn stg-btn--primary" onClick={() => navigate("/food/decide")}>
              <i className="fa-solid fa-wand-magic-sparkles" />ให้ AI เลือกมื้อนี้
            </button>
            <button type="button" className="btn stg-btn stg-btn--ghost food-btn-onhero" onClick={() => navigate("/food/diary")}>
              <i className="fa-solid fa-pen-to-square" />ดูบันทึกวันนี้
            </button>
          </div>
        </div>
        <div className="food-hero-macros">
          {MACROS.map(([key, label, icon, unit, tone]) => (
            <MacroBar key={key} label={label} icon={icon} unit={unit} tone={tone} used={data?.today?.[key] || 0} target={data?.targets?.[key] || 1} compact />
          ))}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-7">
          <div className="stg-panel h-100">
            <div className="stg-panel-head">
              <div className="stg-panel-intro">
                <span className="stg-panel-icon"><i className="fa-solid fa-utensils" /></span>
                <div style={{ minWidth: 0 }}>
                  <p className="stg-eyebrow">Next meal</p>
                  <h3 className="stg-panel-title">{data?.daypart?.title || "มื้อนี้"}กินอะไรดี?</h3>
                  <p className="stg-panel-desc">เลือกโหมดการกินก่อน แล้ว AI จะเลือกเมนูให้พร้อมบอกเหตุผล</p>
                </div>
              </div>
            </div>
            <div className="stg-panel-body">
              {renderQuestion()}

              <div className="food-modes mb-3">
                {eatingModes.map((item) => (
                  <button key={item.key} type="button" className={`food-mode ${mode === item.key ? "is-active" : ""}`} onClick={() => { if (item.key !== mode) load(item.key); }} title={item.hint}>
                    <span className="food-mode-emoji">{item.emoji}</span>
                    <span className="food-mode-label">{item.label}</span>
                  </button>
                ))}
              </div>

              {!data ? (
                <div className="food-pick"><Skeleton lines={5} /></div>
              ) : !pick ? (
                <div className="stg-empty"><i className="fa-regular fa-face-meh" />ไม่มีเมนูที่เข้าเงื่อนไขในโหมดนี้ ลองเปลี่ยนโหมดหรือเพิ่มเมนูในฐานข้อมูล</div>
              ) : (
                <div className="food-pick">
                  <div className="food-pick-head">
                    <span className="food-pick-badge"><i className="fa-solid fa-robot" />AI แนะนำ</span>
                    <span className="food-pick-mode">{activeMode.emoji} {activeMode.label} · {activeMode.hint}</span>
                  </div>
                  <div className="food-pick-body">
                    <span className="food-pick-emoji">{pick.food.emoji}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="food-pick-name">{pick.food.name}</div>
                      <div className="food-pick-stats">
                        <span><strong>{pick.food.kcal}</strong> kcal</span>
                        <span>Protein <strong>{pick.food.protein}</strong> g</span>
                        <span>ประมาณ <strong>฿{pick.food.price}</strong></span>
                      </div>
                      <div className="mt-2 d-flex flex-wrap gap-1">
                        {pick.food.tags.map((tag) => <Chip key={tag} tone="orange">{tag}</Chip>)}
                        <Chip tone="muted" icon={SOURCE_ICONS[pick.food.source]}>{SOURCE_LABELS[pick.food.source]}</Chip>
                      </div>
                    </div>
                  </div>
                  <div className="food-pick-reasons">
                    <div className="food-pick-reasons-title">เหตุผลที่เลือกเมนูนี้</div>
                    <ReasonList reasons={pick.reasons} />
                  </div>
                  <div className="food-pick-actions">
                    <button type="button" className="btn stg-btn stg-btn--primary" onClick={eatThis}><i className="fa-solid fa-check" />กินอันนี้</button>
                    <button type="button" className="btn stg-btn stg-btn--ghost" onClick={another} disabled={busy}><i className={`fa-solid fa-rotate ${busy ? "fa-spin" : ""}`} />ขออีก</button>
                  </div>
                  <FeedbackBar
                    liked={liked}
                    saved={!!pick.saved}
                    onLike={() => { setLiked(true); sendFeedback("like"); }}
                    onDislike={() => { setLiked(false); sendFeedback("dislike"); another(); }}
                    onAnother={another}
                    onSave={toggleSave}
                  />
                  <p className="food-pick-foot"><i className="fa-solid fa-lightbulb" />ทุกการกด ชอบ / ไม่เอา / เก็บไว้ / กินอันนี้ ถูกนำไปปรับคำแนะนำครั้งถัดไปโดยอัตโนมัติ</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <Section icon="fa-regular fa-clock" title={data ? `${data.daypart.emoji} ${data.daypart.title}` : "ตามช่วงเวลา"} desc={data?.daypart?.why || ""} className="mb-3">
            {data ? (
              <div className="d-grid gap-2">
                {data.daypart.foods.map((food, index) => <FoodCard key={food.uid} food={food} rank={index + 1} />)}
              </div>
            ) : <Skeleton />}
          </Section>

          <Section
            icon="fa-solid fa-lightbulb"
            title="AI เสนอเมนูใหม่"
            desc="เมนูที่ยังไม่มีในฐาน คิดจากรูปแบบการกินของคุณ กดรับแล้วระบบจะเอาไปแนะนำด้วย"
            className="mb-3"
            actions={data?.ai_enabled ? (
              <button type="button" className="btn stg-btn stg-btn--ghost stg-btn--sm" onClick={generateSuggestions} disabled={generating || !data.can_generate} title={data.can_generate ? "ขอเมนูใหม่จาก AI (วันละครั้ง)" : "ขอใหม่ได้วันละครั้ง"}>
                <i className={generating ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-wand-magic-sparkles"} />ขอเมนูใหม่
              </button>
            ) : null}
          >
            {!data ? <Skeleton /> : !data.ai_enabled ? (
              <p className="food-tip"><i className="fa-solid fa-plug-circle-xmark" />ยังไม่ได้เปิดใช้ AI ที่ server (ANTHROPIC_API_KEY) ฐานเมนูจึงเพิ่มได้จากหน้าฐานข้อมูลเมนูเท่านั้น</p>
            ) : data.suggestions?.length ? (
              <div className="d-grid gap-2">
                {data.suggestions.map((item) => (
                  <div key={item.uid} className="food-suggest">
                    <div className="food-suggest-main">
                      <span className="food-card-emoji">{item.emoji || "🍽️"}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="food-card-name">{item.name}</div>
                        <div className="food-nutri">
                          <span><i className="fa-solid fa-fire" />{item.kcal} kcal</span>
                          <span><i className="fa-solid fa-drumstick-bite" />P {item.protein} g</span>
                          <span><i className="fa-solid fa-coins" />฿{item.price}</span>
                        </div>
                        {item.ai_note ? <div className="food-suggest-note"><i className="fa-solid fa-robot" />{item.ai_note}</div> : null}
                      </div>
                    </div>
                    <div className="food-suggest-actions">
                      <button type="button" className="btn stg-btn stg-btn--primary stg-btn--sm" onClick={() => decideSuggestion(item, true)}><i className="fa-solid fa-check" />เก็บเข้าฐาน</button>
                      <button type="button" className="btn stg-btn stg-btn--ghost stg-btn--sm" onClick={() => decideSuggestion(item, false)}><i className="fa-solid fa-xmark" />ไม่เอา</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="food-tip"><i className="fa-solid fa-moon" />AI จะเสนอเมนูใหม่ให้อัตโนมัติทุกคืน (ตี 3) เมื่อคุณมีบันทึกมื้อในสัปดาห์นี้ หรือกด "ขอเมนูใหม่" ได้วันละครั้ง</p>
            )}
          </Section>

          <Section icon="fa-solid fa-brain" title="AI เรียนรู้ว่า" desc="รูปแบบการกินที่ระบบสังเกตได้จากบันทึกและปุ่มตอบกลับ" className="mb-3">
            {data?.insights?.length ? (
              <div className="d-grid gap-2">
                {data.insights.map((item) => (
                  <div key={item.text} className="food-insight">
                    <span className="food-insight-icon"><i className={item.icon} /></span>
                    <div><strong>{item.text}</strong><small>{item.detail}</small></div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="food-tip"><i className="fa-solid fa-seedling" />ยังไม่มีข้อมูลพอ กด "กินอันนี้" หรือ ชอบ/ไม่เอา สัก 2-3 วัน ระบบจะเริ่มเห็นรูปแบบการกินของคุณ</p>
            )}
          </Section>

          <Section icon="fa-solid fa-compass" title="ทางลัด" desc="ฟีเจอร์ที่ใช้บ่อย">
            <div className="food-shortcuts">
              <button type="button" className="food-shortcut" onClick={() => navigate("/food/budget")}>
                <span className="food-shortcut-icon"><i className="fa-solid fa-wallet" /></span>
                <span><strong>งบอาหาร</strong><small>{data?.budget?.daily ? `วันนี้ใช้ไป ฿${data.today.price} จาก ฿${data.budget.daily}` : "ตั้งงบต่อวัน/เดือน ระบบติดตามให้อัตโนมัติ"}</small></span>
              </button>
              <button type="button" className="food-shortcut" onClick={() => navigate("/food/profile")}>
                <span className="food-shortcut-icon"><i className="fa-solid fa-user-gear" /></span>
                <span><strong>โปรไฟล์การกิน</strong><small>{data ? `กรอกแล้ว ${data.completeness.percent}% กรอกเพิ่มอีกนิด คำแนะนำจะแม่นขึ้น` : ""}</small></span>
              </button>
              <button type="button" className="food-shortcut" onClick={() => navigate("/food/foods")}>
                <span className="food-shortcut-icon"><i className="fa-solid fa-book" /></span>
                <span><strong>ฐานข้อมูลเมนู</strong><small>เพิ่มเมนูที่กินประจำ ระบบจะเอาไปแนะนำด้วย</small></span>
              </button>
            </div>
          </Section>
        </div>
      </div>

      {/* กรอกครั้งแรก 5 ข้อ ที่เหลือระบบจะถามทีละข้อบนหน้าแรก */}
      <Modal animation={modalAnimation} show={showOnboard} onHide={() => setShowOnboard(false)} centered dialogClassName="stg-modal food-modal">
        <Modal.Header closeButton>
          <div>
            <p className="stg-eyebrow">Welcome</p>
            <Modal.Title>เริ่มต้นแค่ 5 ข้อ</Modal.Title>
            <p className="stg-modal-sub">ระบบจะคำนวณพลังงานและโปรตีนที่ควรได้รับต่อวันให้เอง ที่เหลือค่อยถามทีหลัง</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="food-onboard-hero"><span className="food-thinking-orb"><i className="fa-solid fa-utensils" /></span></div>
          <div className="row g-3">
            <div className="col-6"><label className="stg-label">อายุ (ปี)</label><input type="number" className="form-control" value={onboard.age} onChange={(event) => setOnboard({ ...onboard, age: event.target.value })} /></div>
            <div className="col-6"><label className="stg-label">เพศ</label><Select className="form-control" value={onboard.gender} options={genderOptions} placeholder="เลือก" onChange={(value) => setOnboard({ ...onboard, gender: value })} /></div>
            <div className="col-6"><label className="stg-label">ส่วนสูง (cm)</label><input type="number" className="form-control" value={onboard.height_cm} onChange={(event) => setOnboard({ ...onboard, height_cm: event.target.value })} /></div>
            <div className="col-6"><label className="stg-label">น้ำหนัก (kg)</label><input type="number" className="form-control" value={onboard.weight_kg} onChange={(event) => setOnboard({ ...onboard, weight_kg: event.target.value })} /></div>
            <div className="col-6"><label className="stg-label">เป้าหมาย</label><Select className="form-control" value={onboard.goal} options={goalOptions} onChange={(value) => setOnboard({ ...onboard, goal: value })} /></div>
            <div className="col-6"><label className="stg-label">ออกกำลังกาย</label><Select className="form-control" value={onboard.activity_level} options={activityOptions} onChange={(value) => setOnboard({ ...onboard, activity_level: value })} /></div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowOnboard(false)}>ไว้ทีหลัง</button>
          <button type="button" className="btn stg-btn stg-btn--primary" onClick={saveOnboard}><i className="fa-solid fa-wand-magic-sparkles" />คำนวณเป้าหมายให้ฉัน</button>
        </Modal.Footer>
      </Modal>
    </FoodShell>
  );
}
